/**
 * Performance Monitoring Service
 * Tracks query performance, API calls, cache metrics, and system performance
 */

const PERFORMANCE_STORAGE_KEY = 'admin-performance-metrics';
const MAX_METRICS_HISTORY = 1000;

/**
 * Performance metrics storage
 */
let metricsHistory = [];
let activeQueries = new Map();
let apiCallCounts = new Map();
let cacheMetrics = {
  hits: 0,
  misses: 0,
  size: 0,
  maxSize: 1000
};

/**
 * Initialize performance monitoring
 */
export const initPerformanceMonitoring = () => {
  try {
    const stored = localStorage.getItem(PERFORMANCE_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      metricsHistory = parsed.metricsHistory || [];
      apiCallCounts = new Map(parsed.apiCallCounts || []);
      cacheMetrics = parsed.cacheMetrics || cacheMetrics;
    }
  } catch (error) {
    console.warn('Failed to load performance metrics:', error);
  }
};

/**
 * Save performance metrics to localStorage
 */
const saveMetrics = () => {
  try {
    localStorage.setItem(PERFORMANCE_STORAGE_KEY, JSON.stringify({
      metricsHistory: metricsHistory.slice(-MAX_METRICS_HISTORY),
      apiCallCounts: Array.from(apiCallCounts.entries()),
      cacheMetrics
    }));
  } catch (error) {
    console.warn('Failed to save performance metrics:', error);
  }
};

/**
 * Track query performance
 * @param {string} queryName - Query identifier
 * @param {Function} queryFunction - Query function to execute
 * @returns {Promise} - Query result
 */
export const trackQuery = async (queryName, queryFunction) => {
  const startTime = performance.now();
  const queryId = `${queryName}-${Date.now()}`;
  
  activeQueries.set(queryId, {
    name: queryName,
    startTime,
    status: 'running'
  });

  try {
    const result = await queryFunction();
    const endTime = performance.now();
    const duration = endTime - startTime;

    activeQueries.delete(queryId);

    const metric = {
      type: 'query',
      name: queryName,
      duration,
      timestamp: new Date().toISOString(),
      status: 'success'
    };

    metricsHistory.push(metric);
    if (metricsHistory.length > MAX_METRICS_HISTORY) {
      metricsHistory.shift();
    }

    saveMetrics();
    return result;
  } catch (error) {
    const endTime = performance.now();
    const duration = endTime - startTime;

    activeQueries.delete(queryId);

    const metric = {
      type: 'query',
      name: queryName,
      duration,
      timestamp: new Date().toISOString(),
      status: 'error',
      error: error.message
    };

    metricsHistory.push(metric);
    saveMetrics();

    throw error;
  }
};

/**
 * Track API call
 * @param {string} endpoint - API endpoint
 * @param {string} method - HTTP method
 * @param {number} duration - Request duration in ms
 * @param {number} statusCode - HTTP status code
 */
export const trackAPICall = (endpoint, method = 'GET', duration = 0, statusCode = 200) => {
  const key = `${method}:${endpoint}`;
  const current = apiCallCounts.get(key) || {
    count: 0,
    totalDuration: 0,
    errors: 0,
    lastCall: null
  };

  current.count++;
  current.totalDuration += duration;
  if (statusCode >= 400) {
    current.errors++;
  }
  current.lastCall = new Date().toISOString();

  apiCallCounts.set(key, current);

  const metric = {
    type: 'api',
    endpoint,
    method,
    duration,
    statusCode,
    timestamp: new Date().toISOString()
  };

  metricsHistory.push(metric);
  if (metricsHistory.length > MAX_METRICS_HISTORY) {
    metricsHistory.shift();
  }

  saveMetrics();
};

/**
 * Track cache hit
 */
export const trackCacheHit = () => {
  cacheMetrics.hits++;
  saveMetrics();
};

/**
 * Track cache miss
 */
export const trackCacheMiss = () => {
  cacheMetrics.misses++;
  saveMetrics();
};

/**
 * Update cache size
 * @param {number} size - Current cache size
 */
export const updateCacheSize = (size) => {
  cacheMetrics.size = size;
  saveMetrics();
};

/**
 * Get query performance statistics
 * @param {string} queryName - Optional query name filter
 * @param {number} timeWindow - Time window in minutes (default: 60)
 * @returns {Object} - Performance statistics
 */
export const getQueryStats = (queryName = null, timeWindow = 60) => {
  const cutoffTime = new Date(Date.now() - timeWindow * 60 * 1000).toISOString();
  
  const relevantMetrics = metricsHistory.filter(m => {
    if (m.type !== 'query') return false;
    if (queryName && m.name !== queryName) return false;
    return m.timestamp >= cutoffTime;
  });

  if (relevantMetrics.length === 0) {
    return {
      count: 0,
      avgDuration: 0,
      minDuration: 0,
      maxDuration: 0,
      errorRate: 0
    };
  }

  const durations = relevantMetrics.map(m => m.duration);
  const errors = relevantMetrics.filter(m => m.status === 'error').length;

  return {
    count: relevantMetrics.length,
    avgDuration: durations.reduce((a, b) => a + b, 0) / durations.length,
    minDuration: Math.min(...durations),
    maxDuration: Math.max(...durations),
    errorRate: errors / relevantMetrics.length
  };
};

/**
 * Get API call statistics
 * @param {string} endpoint - Optional endpoint filter
 * @returns {Object} - API statistics
 */
export const getAPIStats = (endpoint = null) => {
  const stats = Array.from(apiCallCounts.entries())
    .filter(([key]) => {
      if (!endpoint) return true;
      return key.includes(endpoint);
    })
    .map(([key, value]) => ({
      endpoint: key,
      count: value.count,
      avgDuration: value.totalDuration / value.count,
      errorRate: value.errors / value.count,
      lastCall: value.lastCall
    }));

  return stats;
};

/**
 * Get cache statistics
 * @returns {Object} - Cache statistics
 */
export const getCacheStats = () => {
  const total = cacheMetrics.hits + cacheMetrics.misses;
  const hitRate = total > 0 ? cacheMetrics.hits / total : 0;

  return {
    hits: cacheMetrics.hits,
    misses: cacheMetrics.misses,
    hitRate,
    size: cacheMetrics.size,
    maxSize: cacheMetrics.maxSize,
    utilization: cacheMetrics.size / cacheMetrics.maxSize
  };
};

/**
 * Get active queries
 * @returns {Array} - Array of active queries
 */
export const getActiveQueries = () => {
  return Array.from(activeQueries.values()).map(query => ({
    ...query,
    duration: performance.now() - query.startTime
  }));
};

/**
 * Get performance summary
 * @param {number} timeWindow - Time window in minutes
 * @returns {Object} - Performance summary
 */
export const getPerformanceSummary = (timeWindow = 60) => {
  const queryStats = getQueryStats(null, timeWindow);
  const apiStats = getAPIStats();
  const cacheStats = getCacheStats();
  const activeQueriesList = getActiveQueries();

  return {
    queries: queryStats,
    api: {
      totalCalls: apiStats.reduce((sum, stat) => sum + stat.count, 0),
      avgDuration: apiStats.length > 0
        ? apiStats.reduce((sum, stat) => sum + stat.avgDuration, 0) / apiStats.length
        : 0,
      endpoints: apiStats
    },
    cache: cacheStats,
    activeQueries: activeQueriesList.length,
    activeQueriesList
  };
};

/**
 * Clear performance metrics
 */
export const clearPerformanceMetrics = () => {
  metricsHistory = [];
  apiCallCounts.clear();
  cacheMetrics = {
    hits: 0,
    misses: 0,
    size: 0,
    maxSize: 1000
  };
  saveMetrics();
};

// Initialize on load
if (typeof window !== 'undefined') {
  initPerformanceMonitoring();
}




